import { createAsyncThunk } from '@reduxjs/toolkit';
import { checkLawFul, CheckLawFulReq, getRefreshToken } from '@services/cgi';
import { generateGuestToken, getGusetUid } from '@utils/helper/user';
import {
  XmppLoginStateEnum,
  setXmppLoginState,
  setVoovLoginState,
  VoovLoginStateEnum,
} from '@store/authentication/authentication.slice';
import { t } from 'i18next';
import { GUEST_APP_ID } from '@constant/index';
import { openToast } from '@component/toast';
import { handleKickout } from '@utils/helper/kickout';
import { hostChangeHandler } from '@utils/helper/hostChange';
import { handlerCoHostChange } from '@utils/helper/coHostChange';
import { handleMeetingSettingsUpdate } from '@utils/helper/meetingSettingsUpdate';
import { unMuteAudio, muteAudio, muteVideo, unMuteVideo } from '@store/media/media.thunk';
import { changeIfShowAgreement, changeIfShowFreeTime, changeRemainTime, showWaitingRoom } from '@store/ui/ui.slice';
import { reportTimeStartXMPPConnect, reportTimeEndXMPPConnect, reportXmppConnectFail } from 'src/report/aegis';
import { gotoHomePage, reload } from '@utils/navigate';
import {
  BOOL,
  MediaType,
  MeetingSettingsPush,
  PushEventMap,
  SiStatus,
  SwitchReason,
  WaitingRoomActiveType,
  XMPPService,
  WeMeetClient,
  CONSTANTS,
} from '@tencent/wemeet-websdk-next';
import { openDialog } from '@component/dialog';
import { setBigVideoAndDisplayModeFromUserList } from '@store/studio/studio.slice';
import {
  setIsHostInMeeting,
  setMeetingSettings,
  setSiStatus,
  setWaitingRoomActiveType,
} from '@store/meeting/meeting.slice';
import { getSelfInfo, setUserList } from '@store/user/user.slice';
import wemeetClient from '@utils/wemeetClient';
import { AppState } from '..';
import { handleMeetingCountdown } from '@utils/helper/meetingCountdown';
import { getAbilityConfig } from '@utils/helper/ability';

export const loginInVoov = createAsyncThunk('authentication/loginInVooV', async (arg, { dispatch }) => {
  dispatch(setVoovLoginState(VoovLoginStateEnum.logging));
  const response = await getRefreshToken({
    refresh_token: 1,
  });

  const { visitorAppUid, deviceId } = await getGusetUid();
  if (response.code !== 0) {
    if (process.env.REACT_APP_DEBUG_TOOL) {
      window.localStorage.setItem('loginInfo', '');
    }
    return {
      app_id: GUEST_APP_ID,
      nick_name: '',
      userid: visitorAppUid,
      wemeet_sign: generateGuestToken(visitorAppUid),
      deviceId,
      fixVideoSwitch: false,
    };
  }

  const config = await getAbilityConfig({
    filters: [
      {
        key: 'web_meeting',
        values: [
          'gray_fix_video_switch', // 修复单流关闭摄像头报错
        ],
      },
    ],
  });

  const fixVideoSwitch = config?.settings?.web_meeting?.gray_fix_video_switch === 1;

  if (process.env.REACT_APP_DEBUG_TOOL) {
    window.localStorage.setItem('loginInfo', JSON.stringify(response.data));
  }

  return {
    ...response.data,
    deviceId,
    fixVideoSwitch,
  };
});

export const xmppLogin = createAsyncThunk<XmppLoginStateEnum, void, { state: AppState }>(
  'authentication/xmppLogin',
  async (arg, { getState, dispatch }) => {
    // thunkAPI.
    const state = getState();
    const { voovAuthInfo } = state.authentication;
    const { xmppLoginState } = state.authentication;

    if (xmppLoginState === XmppLoginStateEnum.succeeded) {
      return XmppLoginStateEnum.succeeded;
    }

    const pushEventsCbs: PushEventMap = {
      USER_JOIN: (data: any) => {
        console.log('PUSH USER_JOIN', data);
        const userList = wemeetClient.userCtrl.getUserInfoList();
        dispatch(setUserList(userList));
        dispatch(setBigVideoAndDisplayModeFromUserList(userList));
      },
      USER_LEAVE: (data: any) => {
        console.log('PUSH USER_LEAVE', data);
        dispatch(setUserList(wemeetClient.userCtrl.getUserInfoList()));
        dispatch(setBigVideoAndDisplayModeFromUserList(wemeetClient.userCtrl.getUserInfoList()));
      },
      USER_UPDATE: (data: any) => {
        console.log('PUSH USER_UPDATE', data);
        dispatch(setUserList(wemeetClient.userCtrl.getUserInfoList()));
        dispatch(setBigVideoAndDisplayModeFromUserList(wemeetClient.userCtrl.getUserInfoList()));
      },
      USER_KICKED: (data: any) => {
        // 没用到 用户被踢出会在SDK触发 UserUpdate
        console.log('PUSH USER_KICKED', data);
        dispatch(setUserList(wemeetClient.userCtrl.getUserInfoList()));
        dispatch(setBigVideoAndDisplayModeFromUserList(wemeetClient.userCtrl.getUserInfoList()));
      },
      USER_OFFLINE: (data: any) => {
        console.log('PUSH USER_OFFLINE', data);
        dispatch(setUserList(wemeetClient.userCtrl.getUserInfoList()));
        dispatch(setBigVideoAndDisplayModeFromUserList(wemeetClient.userCtrl.getUserInfoList()));
      },
      /** @description 自己被踢出的回调 */
      KICKED_OUT: (data: any) => {
        // 自己被踢出的回调
        console.log('PUSH KICKED_OUT', data);
        const currentState = getState();
        const self = getSelfInfo(currentState);
        const isCreator = self?.is_creator === BOOL.true;
        handleKickout(data, isCreator);
      },
      MEETING_COUNTDOWN_PUSH: (data: any) => {
        const currentState = getState();
        const self = getSelfInfo(currentState);
        const isCreator = self?.is_creator === BOOL.true;
        handleMeetingCountdown(data, isCreator);
      },
      HOST_STATUS: (data: any) => {
        console.log('PUSH HOST_STATUS', data);
      },
      WAITING_USER_STATUS: async (data: any) => {
        console.log('PUSH WAITING_USER_STATUS', data);
        const enterRoomType = data?.list?.item?.enter_room_type as WaitingRoomActiveType;
        const hostStatus = data?.host_status?.status;

        switch (enterRoomType) {
          case WaitingRoomActiveType.WAITING_ROOM_TO_MEETING_ROOM_BY_HOST:
            dispatch(setWaitingRoomActiveType(enterRoomType));
            if (hostStatus) dispatch(setIsHostInMeeting(hostStatus === '1'));
            break;
          case WaitingRoomActiveType.MEETING_ROOM_TO_WAITING_ROOM_BY_HOST:
            dispatch(setWaitingRoomActiveType(enterRoomType));
            if (hostStatus) dispatch(setIsHostInMeeting(hostStatus === '1'));
            dispatch(showWaitingRoom());
            return openDialog({
              title: t('主持人已把你移回等候室'),
              okText: t('我知道了'),
            });
            break;
          case WaitingRoomActiveType.WAITING_ROOM_TO_OUTSIDE_BY_HOST:
            dispatch(setWaitingRoomActiveType(enterRoomType));
            break;
          default:
            break;
        }
      },
      MEETING_LIMIT_PUSH: (data: any) => {
        console.log('PUSH MEETING_LIMIT_PUSH', data);
        const limitInfo = data.limit_info;
        const remainingSeconds = Number(limitInfo.remaining_seconds);
        if (remainingSeconds > 0) {
          dispatch(changeIfShowFreeTime(true));
          dispatch(changeRemainTime(remainingSeconds));
        }
      },
      MEETING_WILL_LIMITED: (data: any) => {
        console.log('PUSH MEETING_WILL_LIMITED', data);
      },
      SWITCH_MEDIA_STREAM_NOTIFY: (data: any) => {
        console.log('PUSH SWITCH_MEDIA_STREAM_NOTIFY', data);
        const mediaType = data.type as MediaType;
        const switchOn = data.up_stream_on.__value;
        const reason = data.up_stream_on['@reason'] as SwitchReason;
        const selfInfo = XMPPService.utils.getSelfUserInfo(wemeetClient.xmppService.getUserInfoList());
        const selfAudioOn = selfInfo?.up_audio_on?.__value ?? '';
        const selfVideoOn = selfInfo?.up_video_on?.__value ?? '';

        switch (mediaType) {
          case MediaType.AUDIO:
            /** 被静音 */
            if (reason === SwitchReason.BY_HOST && switchOn === BOOL.false && selfAudioOn === BOOL.true) {
              openToast(t('主持人已将您静音。'));
              dispatch(muteAudio());
            }
            /** 主持人请求开麦 */
            if (reason === SwitchReason.BY_HOST && switchOn === BOOL.true && selfAudioOn !== BOOL.true) {
              // 如果在举手，直接开麦
              if (selfInfo.handsup === BOOL.true) {
                openToast(t('主持人已解除您的静音。'));
              } else if (!WeMeetClient.utils.isHost(selfInfo)) {
                console.log('不是(联席)主持人也没举手，则弹窗询问是否开麦');

                return openDialog({
                  dialogId: 'cancelMute',
                  title: t('解除静音'),
                  content: t('主持人请求解除您的静音。'),
                  okText: t('解除静音'),
                  cancelText: t('保持静音'),
                  onOk: () => {
                    dispatch(unMuteAudio());
                  },
                });
              }
            }
            break;
          case MediaType.VIDEO:
            /** 被停止视频 */
            if (switchOn === BOOL.false && selfVideoOn === BOOL.true) {
              openToast(t('主持人已将您的视频停止'));
              dispatch(muteVideo());
            } else if (switchOn === BOOL.true && (selfVideoOn === BOOL.false || selfVideoOn === '')) {
              openDialog({
                dialogId: 'openVideo',
                title: t('主持人请求您开启视频'),
                // content: t('主持人请求解除您的静音。'),
                okText: t('开启视频'),
                cancelText: t('保持关闭视频'),
                onOk: () => {
                  dispatch(unMuteVideo());
                },
              });
            }
            break;
          default:
            break;
        }
      },
      LOCAL_PERMISSION_NOTIFY: (data: any) => {
        console.log('PUSH LOCAL_PERMISSION_NOTIFY', data);
      },
      HOST_CHANGED: (data: any) => {
        console.log('PUSH HOST_CHANGED', data);
        const userList = wemeetClient.userCtrl.getUserInfoList();
        dispatch(setUserList(userList));

        // FIXME 下面两行真的有用吗？ data 里没有 status 字段
        const hostStatus = data?.status;
        if (hostStatus) dispatch(setIsHostInMeeting(hostStatus === '1'));

        hostChangeHandler(data);
      },
      CO_HOST_CHANGED: (data: any) => {
        console.log('PUSH CO_HOST_CHANGED', data);
        const userList = wemeetClient.userCtrl.getUserInfoList();
        dispatch(setUserList(userList));
        handlerCoHostChange(data);
      },
      HANDS_UP_NOTIFY: (data: any) => {
        console.log('PUSH HANDS_UP_NOTIFY', data);
      },
      HANDS_UP_RESULT: (data: any) => {
        console.log('PUSH HANDS_UP_RESULT', data);
        const audioResult = data?.permission_list.permission?.find((item: any) => item['@type'] === MediaType.AUDIO);
        console.log(audioResult);
        if (audioResult['@is_grant'] === BOOL.true) {
          openToast(t('主持人已解除您的静音。'));
          wemeetClient.mediaCtrl.unMuteAudio({
            needRefreshToken: BOOL.false,
            requestUpStreamToken: '',
          });
        } else {
          openToast(t('主持人已拒绝了您的举手申请。'));
        }
      },
      MEETING_SETTINGS_UPDATED: (data: MeetingSettingsPush) => {
        dispatch(setMeetingSettings(data));
        const selfInfo = XMPPService.utils.getSelfUserInfo(wemeetClient.xmppService.getUserInfoList());
        handleMeetingSettingsUpdate(data, selfInfo);
      },
      HOME_MEETING_ITEM_UPDATE: (data) => console.log('data:', data),
      SCHEDULE: (data: any) => {
        console.log('PUSH SCHEDULE', data);
      },
      MEETING_ITEM_UPDATED: (data: any) => {
        console.log('PUSH MEETING_ITEM_UPDATED', data);
      },
      INVITE_STATUS: (data: any) => {
        console.log('PUSH INVITE_STATUS', data);
      },
      JOIN_AUDIO_MIX_USER_INFO_NOTIFY: (data: any) => {
        console.log('PUSH JOIN_AUDIO_MIX_USER_INFO_NOTIFY', data);
      },
      SI_STATUS: (data: any) => {
        console.log('PUSH SI_STATUS', data);
        const siStatus = data?.si_status as SiStatus;
        if (siStatus) dispatch(setSiStatus(siStatus));
      },
      LIVE_STATUS: (data: any) => {
        console.log('PUSH LIVE_STATUS', data);
      },
      DISCONNECTED: (data: any) => {
        if (data.ecode === 4014) {
          return openDialog({
            dialogId: 'exitMeeting',
            title: t('退出会议'),
            content: t('已通过其他网页进入会议，当前会议已退出。'),
            onOk: () => {
              gotoHomePage();
            },
          });
        }

        // 这里原来的实现逻辑就是直接去到首页
        // 看是否需要进行优化
        if (data.ecode >= 4000 && data.ecode < 5000) {
          return gotoHomePage();
        }

        // TODO: 旧应用层的逻辑是在当前页面进行重连（使用现在的音视频状态重新加入会议），toast提示用户
        // openToast(t('正在连接...'));
        // this.isNetworkFail = true;
        // 自动重新连接 xmpp, 然后进会

        // FIXME: 目前是上报 + 弹窗 + 刷新页面
        return openDialog({
          dialogId: 'xmppExit',
          title: t('已退出会议'),
          content: t('由于电脑睡眠导致 VOOV Meeting 被挂起，已退出上次会议'),
          onOk: () => {
            reload();
          },
        });
      },
    };

    dispatch(setXmppLoginState(XmppLoginStateEnum.logging));

    if (voovAuthInfo) {
      try {
        // 计时开始：XMPP 建联耗时
        reportTimeStartXMPPConnect();

        await wemeetClient.login(
          {
            appId: voovAuthInfo.appid,
            appUid: voovAuthInfo.userid,
            appToken: voovAuthInfo.userSign,
            // TODO: voov login 里有这个信息吗？现在是固定的
            authType:
              voovAuthInfo.appid === GUEST_APP_ID
                ? CONSTANTS.AuthType.AUTH_TYPE_VISITOR
                : CONSTANTS.AuthType.AUTH_TYPE_USER,
            appVersion: process.env.REACT_APP_APP_VERSION,
            envId: ((process.env.REACT_APP_DEBUG_TOOL && window.localStorage.getItem('customCi')) || '').replace(
              '-gdpr',
              '',
            ),
          },
          pushEventsCbs,
        );

        // 计时结束：XMPP 建联耗时
        reportTimeEndXMPPConnect();

        return XmppLoginStateEnum.succeeded;
      } catch (e: any) {
        console.log(e);
        openDialog({
          dialogId: 'xmppConnectFail',
          title: t('发生异常'),
          content: e?.message ?? t('鉴权失败'),
          okText: t('确定'),
          onOk: () => {
            gotoHomePage();
          },
        });

        reportXmppConnectFail(e);

        return XmppLoginStateEnum.failed;
      }
    }
    return XmppLoginStateEnum.idle;
  },
);

/**
 * @description 检查是否要合规
 * { need_check_lawful: boolean; new_auth_code?: string }
 */
export const checklawful = createAsyncThunk<void, string, { state: AppState }>(
  'authentication/checklawful',
  async (authCode = '', { getState, dispatch }) => {
    const state = getState();
    const { voovAuthInfo } = state.authentication;
    const { meetingInfoInput } = state.meeting;
    const params: CheckLawFulReq = {
      lawful_type: 'guest_join',
      device_id: voovAuthInfo?.deviceId ?? '',
      meeting_code: meetingInfoInput.meetingCode,
    };

    if (authCode) {
      params.auth_code = authCode;
    }
    return checkLawFul(params).then((res) => {
      if (res.code === 0) {
        if (res.data.need_check_lawful) {
          dispatch(changeIfShowAgreement(true));
        }
        console.log(res.data);
        return res.data.need_check_lawful;
      }

      return true;
    });
  },
);
